<template>
  <HealthButton
    v-tooltip.bottom="!label ? t('change_appearance') : ''"
    :icon="icon[state]"
    :text-color="color"
    :size="size"
    rounded="rounded-lg"
    :icon-class="iconClass"
    :color-class="colorClass"
    :class="classChangeAppearance"
    :label="label ? t('change_appearance') : undefined"
    :label-class="labelClass"
    :justify="justify"
    @click="usePopoverToggle(popover, $event)"
  />

  <HealthPopover ref="popover" class="dark:bg-dark-800">
    <ul class="flex flex-col gap-3">
      <li
        v-for="(theme, idx) in themes"
        :key="idx"
        class="flex items-center text-base gap-2 hover:text-primary-500 dark:hover:text-primary-100 cursor-pointer"
        :class="
          store === theme.name
            ? 'dark:text-primary-100 text-primary-500'
            : 'dark:text-white text-gray-700'
        "
        @click="changeAppearance($event, theme.name)"
      >
        <Icon :icon="theme.icon" height="22" width="22" />
        <p class="font-semibold">{{ t(theme.name) }}</p>
      </li>
    </ul>
  </HealthPopover>
</template>

<script setup lang="ts">
  import HealthPopover from "@/shared/components/utils/HealthPopover.vue";
  import { ref, nextTick } from "vue";
  import { useI18n } from "vue-i18n";
  import { BasicColorSchema, useColorMode } from "@vueuse/core";
  import { Icon } from "@iconify/vue";
  import { usePopoverHide, usePopoverToggle } from "@/shared/composables/usePopover";
  import HealthButton from "@/shared/components/utils/button/HealthButton.vue";

  const { store, state } = useColorMode({
    initialValue: "light",
  });

  const popover = ref();

  defineProps({
    color: {
      type: String,
      default: "!text-white",
    },
    classChangeAppearance: {
      type: String,
      default: "",
    },
    colorClass: {
      type: String,
      default: "bg-transparent",
    },
    label: {
      type: Boolean,
      default: false,
    },
    labelClass: {
      type: String,
      default: "block font-semibold ml-1",
    },
    justify: {
      type: String,
      default: "justify-center",
    },
    size: {
      type: String,
      default: "px-2.5 py-2.5",
    },
    iconClass: {
      type: String,
      default: "min-w-[1.6rem] min-h-[1.6rem]",
    },
  });

  const { t } = useI18n();

  const themes = ref([
    { name: "light", icon: "lucide:sun" },
    { name: "dark", icon: "f7:moon" },
    { name: "auto", icon: "mi:computer" },
  ]);

  const icon = ref({
    light: "lucide:sun",
    dark: "f7:moon",
  });

  const changeAppearance = (event: Event, theme: string) => {
    usePopoverHide(popover.value, event);

    if (!document.startViewTransition) {
      store.value = theme as BasicColorSchema;
      return;
    }

    document.startViewTransition(async () => {
      store.value = theme as BasicColorSchema;
      await nextTick();
    });
  };
</script>
